import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Ellipsis, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Authorized from "components/Authorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import { useDeleteEmailAttachment } from "hooks/useEmailAttachment";

const EDIT_ACLS = [ACL_RELATIONSHIPS.emailAttachments.edit];
const DELETE_ACLS = [ACL_RELATIONSHIPS.emailAttachments.delete];
const CLONE_ACLS = [ACL_RELATIONSHIPS.emailAttachments.create];
const ANY_ACLS = [...EDIT_ACLS, ...DELETE_ACLS, ...CLONE_ACLS];

export const EmailAttachmentsTableActions = ({ original }) => {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { mutateAsync: deleteAttachment } = useDeleteEmailAttachment();

  const handleDelete = async () => {
    try {
      await deleteAttachment(original.id);
      toast.success("Email attachment deleted");
      setDeleteOpen(false);
    } catch {
      toast.error("Failed to delete email attachment");
    }
  };

  return (
    <Authorized permissions={ANY_ACLS} partialMatch>
      <>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Email attachment actions"
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <Ellipsis />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="tw:w-auto">
            <Authorized permissions={EDIT_ACLS}>
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/settings/email_attachments/${original.id}`)
                }
              >
                <Pencil />
                Edit
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={CLONE_ACLS}>
              <DropdownMenuItem
                onClick={() =>
                  navigate(
                    `/settings/email_attachments/new?clone=${original.id}`
                  )
                }
              >
                <Copy />
                Clone
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={DELETE_ACLS}>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </Authorized>
          </DropdownMenuContent>
        </DropdownMenu>
        <Authorized permissions={DELETE_ACLS}>
          <ConfirmDeleteDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title="Delete email attachment?"
            description={`Are you sure you want to delete the email attachment "${original.name}"? This action cannot be undone.`}
            onConfirm={handleDelete}
          />
        </Authorized>
      </>
    </Authorized>
  );
};
